import { Bot, CheckCircle2, Command, Layers3, MousePointerClick, Network, RadioTower, Route, ShieldCheck, SlidersHorizontal, Sparkles } from 'lucide-react';
import { careevaDesignSystem, componentHierarchy, miniAppFirstFlows } from '../../ux/designSystem.js';
import { useAppStore } from '../../store.js';

const iconMap = {
  quick: MousePointerClick,
  advanced: SlidersHorizontal,
  pro: Layers3
};

function MiniMetric({ label, value }) {
  return <div className="ux-mini-metric"><span>{label}</span><strong>{value}</strong></div>;
}

export function UnifiedDesignSystemPanel() {
  const mode = useAppStore((s) => s.mode);
  const current = careevaDesignSystem.modes[mode] || careevaDesignSystem.modes.quick;
  const Icon = iconMap[mode] || MousePointerClick;
  return (
    <section className="ux-system-panel glass">
      <div className="ux-panel-head">
        <div><span className="eyebrow">Unified Design System</span><h2>Careeva Interface OS</h2></div>
        <span className="ux-mode-pill"><Icon size={15} /> {current.label}</span>
      </div>
      <p className="ux-lead">{current.promise}. {current.explanation}</p>
      <div className="ux-metric-grid">
        <MiniMetric label="Density" value={current.density} />
        <MiniMetric label="Primary actions" value={`≤ ${current.maxPrimaryActions}`} />
        <MiniMetric label="Menu depth" value={`≤ ${current.navigationDepth}`} />
        <MiniMetric label="Core rule" value="Next best step" />
      </div>
      <div className="ux-token-row">
        {Object.entries(careevaDesignSystem.tokens.color).slice(3, 10).map(([name, value]) => <span key={name} className="ux-token" style={{ '--token': value }}>{name}</span>)}
      </div>
    </section>
  );
}

export function NavigationArchitecturePanel({ productMode = 'career' }) {
  const nav = careevaDesignSystem.navigation[productMode] || careevaDesignSystem.navigation.career;
  return (
    <section className="ux-architecture-grid">
      <div className="ux-system-panel glass">
        <div className="ux-panel-head"><div><span className="eyebrow">Navigation Architecture</span><h3>5 глобальных зон вместо deep menu</h3></div><Route size={20} /></div>
        <div className="ux-flow-map">
          {Object.entries(nav).map(([zone, children]) => (
            <div className="ux-flow-node" key={zone}>
              <strong>{zone}</strong>
              <span>{children.join(' · ')}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="ux-system-panel glass">
        <div className="ux-panel-head"><div><span className="eyebrow">Component Hierarchy</span><h3>От Shell до Inspector</h3></div><Network size={20} /></div>
        <div className="ux-hierarchy">
          {componentHierarchy.map(([name, text], index) => <div className="ux-hierarchy-row" key={name}><b>{index + 1}</b><div><strong>{name}</strong><span>{text}</span></div></div>)}
        </div>
      </div>
    </section>
  );
}

export function AiInteractionLanguagePanel({ aiStatus, aiProduction }) {
  const language = careevaDesignSystem.aiLanguage;
  const queueCount = (aiProduction?.queue || []).reduce((sum, item) => sum + Number(item.count || 0), 0);
  const enabled = Boolean(aiStatus?.enabled);
  return (
    <section className="ux-system-panel glass">
      <div className="ux-panel-head"><div><span className="eyebrow">AI Interaction Language</span><h3>Понятные состояния вместо технического шума</h3></div><Bot size={20} /></div>
      <div className="ux-ai-grid">
        <div className="ux-ai-card active"><Sparkles size={17} /><strong>{enabled ? language.thinking : language.fallback}</strong><span>{aiStatus?.provider || 'local'} · {aiStatus?.model || 'fallback'}</span></div>
        <div className="ux-ai-card"><RadioTower size={17} /><strong>{language.ranking}</strong><span>Queue: {queueCount}</span></div>
        <div className="ux-ai-card"><ShieldCheck size={17} /><strong>{language.askingApproval}</strong><span>Never auto-send risky actions</span></div>
      </div>
    </section>
  );
}

export function MiniAppFirstFlowsPanel() {
  return (
    <section className="ux-system-panel glass">
      <div className="ux-panel-head"><div><span className="eyebrow">MiniApp-first flows</span><h3>Сложные сценарии в 2–5 кликов</h3></div><MousePointerClick size={20} /></div>
      <div className="ux-flow-cards">
        {miniAppFirstFlows.map((flow) => <article className="ux-flow-card" key={flow.id}><div><strong>{flow.name}</strong><span>{flow.mode} · target {flow.targetClicks} clicks</span></div><ol>{flow.steps.map((step) => <li key={step}>{step}</li>)}</ol></article>)}
      </div>
    </section>
  );
}

export function StateVisualizationPanel({ lastError, actionPending, apiSource }) {
  const state = lastError ? 'risk' : actionPending ? 'running' : apiSource === 'mock' ? 'fallback' : 'done';
  return (
    <section className="ux-system-panel glass">
      <div className="ux-panel-head"><div><span className="eyebrow">State Visualization</span><h3>Единая модель состояний</h3></div><CheckCircle2 size={20} /></div>
      <div className="ux-state-grid">
        {careevaDesignSystem.stateModel.map(([id, text, tone]) => <div className={`ux-state-pill ${tone} ${id === state ? 'selected' : ''}`} key={id}><strong>{id}</strong><span>{text}</span></div>)}
      </div>
    </section>
  );
}

export function CommandDrivenModelPanel({ commandCenter }) {
  const suggestions = commandCenter?.suggestions?.length ? commandCenter.suggestions : careevaDesignSystem.commandModel;
  const runCommand = useAppStore((s) => s.runCommand);
  return (
    <section className="ux-system-panel glass">
      <div className="ux-panel-head"><div><span className="eyebrow">Command-driven interaction model</span><h3>Одна строка вместо меню и подменю</h3></div><Command size={20} /></div>
      <div className="ux-command-list">
        {suggestions.slice(0, 6).map((item) => <button key={item} onClick={() => runCommand(item)}><Command size={14} /><span>{item}</span></button>)}
      </div>
    </section>
  );
}

export function CrossPlatformConsistencyPanel() {
  return (
    <section className="ux-system-panel glass">
      <div className="ux-panel-head"><div><span className="eyebrow">Cross-platform UX consistency</span><h3>Telegram MiniApp ↔ Browser ↔ Bot</h3></div><Layers3 size={20} /></div>
      <div className="ux-platform-grid">
        <div><strong>Telegram Bot</strong><span>Notifications, approve, summaries, deep links</span></div>
        <div><strong>MiniApp</strong><span>Daily mission, quick review, CV upload, approval queue</span></div>
        <div><strong>Browser</strong><span>Advanced filters, Pro inspector, analytics, bulk operations</span></div>
      </div>
    </section>
  );
}

export function CareevaUxSystem({ productMode, aiStatus, aiProduction, lastError, actionPending, apiSource, commandCenter }) {
  const mode = useAppStore((s) => s.mode);
  return (
    <section className={`ux-system-shell ux-${mode}`}>
      <UnifiedDesignSystemPanel />
      {mode !== 'quick' && <NavigationArchitecturePanel productMode={productMode} />}
      {mode !== 'quick' && <MiniAppFirstFlowsPanel />}
      {mode === 'pro' && <AiInteractionLanguagePanel aiStatus={aiStatus} aiProduction={aiProduction} />}
      {mode === 'pro' && <StateVisualizationPanel lastError={lastError} actionPending={actionPending} apiSource={apiSource} />}
      {mode === 'pro' && <CommandDrivenModelPanel commandCenter={commandCenter} />}
      {mode === 'pro' && <CrossPlatformConsistencyPanel />}
    </section>
  );
}
